import { useEffect, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { PreparingReportLoader } from "@/components/preparing-report-loader";
import * as chatService from "@/services/chat";
import { resolveToken, type ReportLink } from "@/services/report-links";
import {
  getMotilityVideo,
  getReportSummary,
  type BiomarkerSummary,
} from "@/services/report-summary";
import { getTest } from "@/services/tests";
import type { ChatSession, Test } from "@/types";
import { ReportContextProvider } from "./context";

/**
 * Loads everything the patient report shell needs ONCE per token:
 *   - resolveToken (and bumps access_count server-side)
 *   - test metadata
 *   - summary biomarkers
 *   - motility video URL
 *   - chat session list
 *
 * Children (summary, chat layout, individual chat session) read from
 * the shared context and never refetch this data when navigating between
 * `/r/:token`, `/r/:token/s/:sessionId`, etc.
 */
export default function PatientReportLayout() {
  const { token } = useParams<{ token: string }>();

  const [link, setLink] = useState<ReportLink | null>(null);
  const [test, setTest] = useState<Test | null>(null);
  const [good, setGood] = useState<BiomarkerSummary[]>([]);
  const [attention, setAttention] = useState<BiomarkerSummary[]>([]);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const resolved = await resolveToken(token);
        if (cancelled) return;

        const [t, summary, video, sessionList] = await Promise.all([
          getTest(resolved.test_id),
          getReportSummary(resolved.test_id, token),
          getMotilityVideo(resolved.test_id, token).catch(() => ({
            url: null,
            exists: false,
          })),
          chatService.listSessions(token),
        ]);
        if (cancelled) return;

        setLink(resolved);
        setTest(t);
        setGood(summary.good);
        setAttention(summary.attention);
        setVideoUrl(video.exists && video.url ? video.url : null);
        setSessions(sessionList);
      } catch {
        if (!cancelled) setError("This link is invalid or has expired.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  if (loading) {
    return <PreparingReportLoader fullScreen />;
  }

  if (error || !token || !link || !test) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center gap-4 bg-surface">
        <p className="text-muted">{error ?? "Report not found."}</p>
      </div>
    );
  }

  return (
    <ReportContextProvider
      value={{ token, link, test, good, attention, videoUrl, sessions, setSessions }}
    >
      <Outlet />
    </ReportContextProvider>
  );
}
